import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableCell } from '@tiptap/extension-table/cell';
import { TableHeader } from '@tiptap/extension-table/header';
import { TableRow } from '@tiptap/extension-table/row';
import { Table as DefaultTable } from '@tiptap/extension-table/table';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import MenuBar from './components/MenuBar';
import TableToolbar from './components/TableToolbar';

const TiptapStyle = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    border: 1px solid var(--silver);
    border-radius: 4px;

    &.disabled {
        background-color: #eee;
    }

    .markdown-contents {
        padding: 0 25px;
        overflow: auto;
        display: flex;
        flex-grow: 1;
        min-height: 6em;
    }

    .tiptap {
        flex-grow: 1;
        outline: none;

        table {
            td {
                position: relative;

                &.selectedCell {
                    > * {
                        position: relative;
                        z-index: 3;
                    }

                    &::after {
                        content: '';
                        background: var(--dove);
                        inset: 0;
                        pointer-events: none;
                        position: absolute;
                        z-index: 2;
                    }
                }
            }
        }
    }

    button,
    .btn-xs {
        border: 1px solid transparent;
        border-radius: 3px;
        font-size: inherit;
        background-color: #efefef;
        color: var(--cinder);

        &:hover:not([disabled]) {
            background-color: var(--dove);
        }
    }

    .table-toolbar {
        background-color: white;
        border-top: 1px solid var(--dove);
        display: flex;
        overflow-x: auto;
        font-size: 0.9em;
        padding: 0.25rem 1.5rem;

        button {
            flex-shrink: 0;
            white-space: nowrap;
            margin-right: 3px;

            &:last-of-type {
                margin-right: 0;
            }
        }
    }
`;

const StyledEditorContent = styled(EditorContent)`
    display: flex;
    flex-grow: 1;
`;

const Table = DefaultTable.extend({
    renderHTML({ HTMLAttributes }) {
        return ['table', HTMLAttributes, ['tbody', 0]];
    },
});

const Tiptap = ({
    name,
    value,
    onChange,
    onBlur,
    className,
    style,

    disabled,
    debounce: enableDebounce,
}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- linter doesn't recognize format
    const debouncedOnChange = useCallback(debounce(onChange, 200), [onChange]);

    const handleChange = (newValue) => {
        const changeFn = enableDebounce ? debouncedOnChange : onChange;
        changeFn(newValue);
    };

    const editor = useEditor({
        extensions: [StarterKit, Table, TableRow, TableHeader, TableCell, Markdown, Superscript, Subscript, Highlight],
        editorProps: {
            attributes: {
                id: name,
                name,
            },
        },
        contentType: 'markdown',

        content: value,
        onUpdate: ({ editor: e }) => {
            if (disabled) return;
            handleChange(e.getHTML());
        },
        onBlur: ({ event }) => {
            if (onBlur) {
                onBlur(event);
            }
        },
        editable: !disabled,
    });

    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    return (
        <TiptapStyle className={cx(className, { disabled })} style={style}>
            <MenuBar editor={editor} />

            <div className="markdown-contents">
                <StyledEditorContent editor={editor} />
            </div>

            <TableToolbar editor={editor} />
        </TiptapStyle>
    );
};

Tiptap.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.shape({}),

    disabled: PropTypes.bool,
    debounce: PropTypes.bool,
};

Tiptap.defaultProps = {
    value: '',
    onBlur: null,
    className: '',
    style: null,

    disabled: false,
    debounce: false,
};

export default Tiptap;
